import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import {
  newActivitySchema,
  ACTIVITY_CATEGORIES,
  type NewActivityFormValues,
  type NewActivityFormInput,
} from '@/utils/schemas'
import { searchPlaces, type PlaceSuggestion } from '@/services/placesService'

interface ActivityFormProps {
  defaultValues?: Partial<NewActivityFormInput>
  onSubmit: (values: NewActivityFormValues) => void
  onCancel: () => void
  isPending: boolean
  isError: boolean
  submitLabel: string
}

export function ActivityForm({
  defaultValues,
  onSubmit,
  onCancel,
  isPending,
  isError,
  submitLabel,
}: ActivityFormProps) {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<NewActivityFormInput, unknown, NewActivityFormValues>({
    resolver: zodResolver(newActivitySchema),
    defaultValues,
  })

  const locationValue = watch('location')
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([])
  const skipNextSearchRef = useRef(false)

  useEffect(() => {
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false
      return
    }
    if (!locationValue || locationValue.trim().length < 3) {
      setSuggestions([])
      return
    }
    const timeout = setTimeout(async () => {
      try {
        setSuggestions(await searchPlaces(locationValue))
      } catch {
        setSuggestions([])
      }
    }, 600)
    return () => clearTimeout(timeout)
  }, [locationValue])

  const selectSuggestion = (suggestion: PlaceSuggestion) => {
    skipNextSearchRef.current = true
    setValue('location', suggestion.displayName)
    setValue('latitude', String(suggestion.latitude))
    setValue('longitude', String(suggestion.longitude))
    setSuggestions([])
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-3 rounded-md border border-line-strong bg-panel p-4"
    >
      <input
        aria-label={t('itinerary.activityTitle')}
        placeholder={t('itinerary.activityTitle')}
        className="rounded border border-line-strong bg-panel-2 px-3 py-2 text-ivory"
        {...register('title')}
      />
      {errors.title && (
        <p role="alert" className="text-sm text-brass-warm">
          {t('itinerary.titleRequired')}
        </p>
      )}
      <select
        aria-label={t('itinerary.selectCategory')}
        className="rounded border border-line-strong bg-panel-2 px-3 py-2 text-ivory"
        {...register('category')}
      >
        <option value="">{t('itinerary.selectCategory')}</option>
        {ACTIVITY_CATEGORIES.map((cat) => (
          <option key={cat} value={cat}>
            {t(`itinerary.category.${cat}`)}
          </option>
        ))}
      </select>
      <div className="grid grid-cols-2 gap-3">
        <input
          type="time"
          aria-label={t('itinerary.startTime')}
          className="rounded border border-line-strong bg-panel-2 px-3 py-2 text-ivory"
          {...register('startTime')}
        />
        <input
          type="time"
          aria-label={t('itinerary.endTime')}
          className="rounded border border-line-strong bg-panel-2 px-3 py-2 text-ivory"
          {...register('endTime')}
        />
      </div>
      <div className="relative">
        <input
          aria-label={t('itinerary.location')}
          placeholder={t('itinerary.location')}
          autoComplete="off"
          className="w-full rounded border border-line-strong bg-panel-2 px-3 py-2 text-ivory"
          {...register('location')}
        />
        {suggestions.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full rounded border border-line-strong bg-panel-2 shadow-lg">
            {suggestions.map((suggestion) => (
              <li key={`${suggestion.latitude},${suggestion.longitude}`}>
                <button
                  type="button"
                  onClick={() => selectSuggestion(suggestion)}
                  className="block w-full px-3 py-2 text-left text-sm text-ivory hover:bg-panel"
                >
                  {suggestion.displayName}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <input type="hidden" {...register('latitude')} />
      <input type="hidden" {...register('longitude')} />
      <input
        aria-label={t('itinerary.note')}
        placeholder={t('itinerary.note')}
        className="rounded border border-line-strong bg-panel-2 px-3 py-2 text-ivory"
        {...register('note')}
      />
      {isError && (
        <p role="alert" className="text-sm text-brass-warm">
          {t('itinerary.saveError')}
        </p>
      )}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-line-strong px-4 py-2 font-sans text-sm text-ivory"
        >
          {t('itinerary.cancel')}
        </button>
        <button
          type="submit"
          disabled={isPending}
          className="rounded border border-brass bg-brass px-4 py-2 font-sans text-sm font-medium text-night disabled:opacity-50"
        >
          {submitLabel}
        </button>
      </div>
    </form>
  )
}
